import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Phone,
  Mail,
  FileText,
  RefreshCw,
  Calendar,
  Filter,
  ChevronLeft,
  ChevronRight,
  Building2,
  User,
  Clock,
  Search,
} from 'lucide-react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
} from '@/components/ui'
import { activitiesApi } from '@/api/endpoints/activities'
import type { Activity, ActivityFilters } from '@/types'
import { cn, formatDate, formatTime, formatRelativeTime } from '@/lib/utils'

const ACTIVITY_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'call', label: 'Calls' },
  { value: 'email', label: 'Emails' },
  { value: 'note', label: 'Notes' },
  { value: 'status_change', label: 'Status Changes' },
]

const PAGE_SIZES = [25, 50, 100]

const getActivityIcon = (type: Activity['type']) => {
  switch (type) {
    case 'call':
      return Phone
    case 'email':
      return Mail
    case 'note':
      return FileText
    case 'status_change':
      return RefreshCw
    default:
      return FileText
  }
}

const getActivityColor = (type: Activity['type']) => {
  switch (type) {
    case 'call':
      return 'bg-green-100 text-green-700 border-green-200'
    case 'email':
      return 'bg-blue-100 text-blue-700 border-blue-200'
    case 'note':
      return 'bg-yellow-100 text-yellow-700 border-yellow-200'
    case 'status_change':
      return 'bg-purple-100 text-purple-700 border-purple-200'
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200'
  }
}

const getActivityBadgeColor = (type: Activity['type']) => {
  switch (type) {
    case 'call':
      return 'bg-green-500'
    case 'email':
      return 'bg-blue-500'
    case 'note':
      return 'bg-yellow-500'
    case 'status_change':
      return 'bg-purple-500'
    default:
      return 'bg-gray-500'
  }
}

// Use getRelativeTime as alias for formatRelativeTime for backwards compatibility
const getRelativeTime = formatRelativeTime

// Group activities by date
const groupActivitiesByDate = (activities: Activity[]) => {
  const groups: { [key: string]: Activity[] } = {}

  activities.forEach(activity => {
    const date = formatDate(activity.created_at)
    if (!groups[date]) {
      groups[date] = []
    }
    groups[date].push(activity)
  })

  return groups
}

export function Activities() {
  const [filters, setFilters] = useState<ActivityFilters>({
    page: 1,
    page_size: 25,
  })
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  // Build filters for API
  const apiFilters = useMemo(() => {
    const f: ActivityFilters = {
      page: filters.page,
      page_size: filters.page_size,
    }
    if (typeFilter !== 'all') f.type = typeFilter
    if (dateFrom) f.date_from = dateFrom
    if (dateTo) f.date_to = dateTo
    return f
  }, [filters, typeFilter, dateFrom, dateTo])

  // Fetch activities
  const { data, isLoading, error } = useQuery({
    queryKey: ['activities', apiFilters],
    queryFn: () => activitiesApi.getAll(apiFilters),
  })

  const activities = data?.data?.items || []
  const totalPages = data?.data?.total_pages || 1
  const totalItems = data?.data?.total || 0

  // Filter activities by search (client-side for description search)
  const filteredActivities = useMemo(() => {
    if (!searchQuery.trim()) return activities
    const query = searchQuery.toLowerCase()
    return activities.filter(
      (activity: Activity) =>
        (activity.notes || activity.subject || activity.description || '').toLowerCase().includes(query) ||
        activity.lead?.company_name?.toLowerCase().includes(query) ||
        activity.lead?.contact_name?.toLowerCase().includes(query)
    )
  }, [activities, searchQuery])

  // Group filtered activities by date
  const groupedActivities = useMemo(() => {
    return groupActivitiesByDate(filteredActivities)
  }, [filteredActivities])

  const dateGroups = Object.keys(groupedActivities).sort((a, b) => {
    return new Date(b).getTime() - new Date(a).getTime()
  })

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }))
  }

  const handlePageSizeChange = (size: string) => {
    setFilters(prev => ({ ...prev, page_size: parseInt(size), page: 1 }))
  }

  const clearFilters = () => {
    setTypeFilter('all')
    setSearchQuery('')
    setDateFrom('')
    setDateTo('')
    setFilters({ page: 1, page_size: 25 })
  }

  const hasActiveFilters = typeFilter !== 'all' || searchQuery || dateFrom || dateTo

  if (error) {
    return (
      <div className="p-6">
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <p className="text-destructive">Failed to load activities. Please try again.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Activity Timeline</h1>
          <p className="text-muted-foreground">
            Track all activities across your leads
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
            {/* Search */}
            <div className="relative lg:col-span-2">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search activities..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Type Filter */}
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Activity Type" />
              </SelectTrigger>
              <SelectContent>
                {ACTIVITY_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Date From */}
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                type="date"
                placeholder="From date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Date To */}
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                type="date"
                placeholder="To date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          {hasActiveFilters && (
            <div className="mt-4 flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Active filters:</span>
              {typeFilter !== 'all' && (
                <Badge variant="secondary">{ACTIVITY_TYPES.find(t => t.value === typeFilter)?.label}</Badge>
              )}
              {searchQuery && <Badge variant="secondary">Search: {searchQuery}</Badge>}
              {dateFrom && <Badge variant="secondary">From: {dateFrom}</Badge>}
              {dateTo && <Badge variant="secondary">To: {dateTo}</Badge>}
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                Clear all
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats Summary */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Phone className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {activities.filter(a => a.type === 'call').length}
                </p>
                <p className="text-sm text-muted-foreground">Calls</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Mail className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {activities.filter(a => a.type === 'email').length}
                </p>
                <p className="text-sm text-muted-foreground">Emails</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <FileText className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {activities.filter(a => a.type === 'note').length}
                </p>
                <p className="text-sm text-muted-foreground">Notes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <RefreshCw className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {activities.filter(a => a.type === 'status_change').length}
                </p>
                <p className="text-sm text-muted-foreground">Status Changes</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Timeline */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Timeline
              <Badge variant="outline" className="ml-2">{totalItems} activities</Badge>
            </CardTitle>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Show:</span>
              <Select
                value={String(filters.page_size)}
                onValueChange={handlePageSizeChange}
              >
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZES.map(size => (
                    <SelectItem key={size} value={String(size)}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="flex gap-4 animate-pulse">
                  <div className="w-10 h-10 bg-muted rounded-full" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-muted rounded w-3/4" />
                    <div className="h-3 bg-muted rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredActivities.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-medium text-lg">No activities found</h3>
              <p className="text-muted-foreground">
                {hasActiveFilters
                  ? 'Try adjusting your filters'
                  : 'Activities will appear here as you interact with leads'}
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {dateGroups.map(date => (
                <div key={date}>
                  {/* Date Header */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-px flex-1 bg-border" />
                    <span className="text-sm font-medium text-muted-foreground bg-background px-2">
                      {date}
                    </span>
                    <div className="h-px flex-1 bg-border" />
                  </div>

                  {/* Activities for this date */}
                  <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute left-5 top-0 bottom-0 w-px bg-border" />

                    <div className="space-y-4">
                      {groupedActivities[date].map((activity: Activity) => {
                        const activityType = activity.activity_type || activity.type || 'unknown'
                        const Icon = getActivityIcon(activityType)
                        const colorClass = getActivityColor(activityType)
                        const badgeColor = getActivityBadgeColor(activityType)

                        return (
                          <div key={activity.id} className="relative flex gap-4 group">
                            {/* Icon */}
                            <div
                              className={cn(
                                'relative z-10 flex items-center justify-center w-10 h-10 rounded-full border-2 bg-background transition-shadow group-hover:shadow-md',
                                colorClass
                              )}
                            >
                              <Icon className="h-4 w-4" />
                            </div>

                            {/* Content */}
                            <div className="flex-1 pb-4">
                              <div className="bg-muted/50 rounded-lg p-4 hover:bg-muted transition-colors">
                                <div className="flex items-start justify-between gap-4">
                                  <div className="flex-1 min-w-0">
                                    {/* Activity Type Badge */}
                                    <div className="flex items-center gap-2 mb-2">
                                      <Badge className={cn('text-white', badgeColor)}>
                                        {activityType.replace('_', ' ')}
                                      </Badge>
                                      <span className="text-xs text-muted-foreground">
                                        {formatTime(activity.created_at)}
                                      </span>
                                      <span className="text-xs text-muted-foreground">
                                        ({getRelativeTime(activity.created_at)})
                                      </span>
                                    </div>

                                    {/* Description/Notes */}
                                    <p className="text-sm font-medium">
                                      {activity.notes || activity.subject || activity.description || 'No description'}
                                    </p>

                                    {/* Outcome */}
                                    {activity.outcome && (
                                      <p className="text-xs text-muted-foreground mt-1">
                                        Outcome: {activity.outcome}
                                      </p>
                                    )}

                                    {/* Lead Info */}
                                    {activity.lead && (
                                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                                        <span className="flex items-center gap-1">
                                          <Building2 className="h-3 w-3" />
                                          {activity.lead.company_name}
                                        </span>
                                        {activity.lead.contact_name && (
                                          <span className="flex items-center gap-1">
                                            <User className="h-3 w-3" />
                                            {activity.lead.contact_name}
                                          </span>
                                        )}
                                      </div>
                                    )}

                                    {/* Metadata */}
                                    {activity.details && Object.keys(activity.details).length > 0 && (
                                      <div className="mt-2 text-xs text-muted-foreground">
                                        <details className="cursor-pointer">
                                          <summary className="hover:text-foreground">
                                            View details
                                          </summary>
                                          <pre className="mt-2 p-2 bg-background rounded text-xs overflow-auto">
                                            {JSON.stringify(activity.details, null, 2)}
                                          </pre>
                                        </details>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                Page {filters.page} of {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(filters.page! - 1)}
                  disabled={filters.page === 1}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(filters.page! + 1)}
                  disabled={filters.page === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default Activities
